import { useEffect, useState, useCallback } from "react"
import { number } from "prop-types"
import { api } from "utils/api"
import { Router, navigate } from "components/ui"
import SelectionTypesList from "./SelectionTypesList"
import SelectionTypeEdit from "./SelectionTypeEdit"
import SelectionTypeNew from "./SelectionTypeNew"
import useApiRecord from "hooks/use_api_record"
import useSelectionTypes from "./useSelectionTypes"
import { SELECTION_TYPE_PARAMS, SIGNUP_PARAMS } from "./requestParams"

const SelectionTypes = ({ signup_id }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [selectionTypes, setSelectionTypes] = useState([])

  const { loading: signupLoading, record: signup } = useApiRecord({
    id: signup_id,
    path: "events",
    data: SIGNUP_PARAMS
  })

  const loading = signupLoading || isLoading

  const selectionTypesResource = useSelectionTypes({
    signupId: signup_id,
    data: SELECTION_TYPE_PARAMS
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    const resp = await selectionTypesResource.index({ walk: true })
    setSelectionTypes(sortSelectionTypes(resp.data))
    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleWaitlistSave = attrs => {
    return updateSelectionType(attrs)
  }

  const handleCapacitySave = async ({ totalCapacity, changes }) => {
    let requests = []
    if (totalCapacity != signup?.maximumCapacity) {
      await signup.update({ maximum_capacity: totalCapacity })
    }

    Object.values(changes).forEach(change => {
      requests.push(updateSelectionType(change))
    })

    const values = await Promise.all(requests)
    handleSelectionTypeCollectionUpdates(values.map(v => v.data))
  }

  const handleDragEnd = async newItems => {
    await api.put(`/signups/${signup_id}/attendee_types/sort`, {
      order: newItems.filter(x => x.id !== "footer").map(x => x.id)
    })
    const resp = await selectionTypesResource.index()
    handleSelectionTypeCollectionUpdates(resp.data)
  }

  const updateSelectionType = async ({ id, ...attributes }) => {
    const resp = await selectionTypesResource.update(id, attributes)

    const updatedSelections = selectionTypes.map(st =>
      st.id === resp.data.id ? { ...st, ...resp.data } : st
    )

    setSelectionTypes(sortSelectionTypes(updatedSelections))
    return resp
  }

  const handleUpdate = (attrs, selectionType) => {
    return updateSelectionType({ id: selectionType.id, ...attrs })
      .then(resp => handleSelectionTypeCollectionUpdates(resp.data))
      .then(_ => showSuccessModal())
  }

  const handleCreate = (attrs, _attendeeType) => {
    return selectionTypesResource
      .create(attrs)
      .then(resp => handleSelectionTypeCollectionUpdates(resp.data))
      .then(_ => showSuccessModal())
  }

  const handleSelectionTypeCollectionUpdates = updatedSelectionTypes => {
    const changedSelections = Array.isArray(updatedSelectionTypes)
      ? updatedSelectionTypes
      : [updatedSelectionTypes]
    const unchangedSelections = selectionTypes.filter(
      st => !changedSelections.map(v => Number(v.id)).includes(Number(st.id))
    )
    return setSelectionTypes(sortSelectionTypes([...unchangedSelections, ...changedSelections]))
  }

  const showSuccessModal = () => {
    return alertModal(
      {
        title: "Selection saved!",
        type: "success",
        showCancelButton: false,
        confirmButtonText: "Ok",
        closeOnConfirm: true
      },
      () => navigateIfFeatureEnabled()
    )
  }

  const handleCancel = () => {
    return navigateIfFeatureEnabled()
  }

  const handleDelete = selectionType => {
    return selectionTypesResource
      .destroy(selectionType.id)
      .then(_ => setSelectionTypes(selectionTypes.filter(at => at.id !== selectionType.id)))
      .then(_ => navigateIfFeatureEnabled())
  }

  const sortSelectionTypes = (types = []) => {
    return types.sort((a, b) => {
      return a.position - b.position
    })
  }

  const navigateIfFeatureEnabled = () => {
    return navigate(`/signups/${signup.id}/attendee_types`)
  }

  return loading ? null : (
    <Router primary={false}>
      <SelectionTypesList
        path="signups/:eventId/attendee_types"
        signup={signup}
        selectionTypes={selectionTypes}
        onWaitlistSave={handleWaitlistSave}
        onCapacitySave={handleCapacitySave}
        onDragEnd={handleDragEnd}
        signupTimes={signup.eventTimes}
        isLoading={isLoading}
      />

      <SelectionTypeNew
        path="signups/:eventId/attendee_types/new"
        onCancel={handleCancel}
        onSave={handleCreate}
        signup={signup}
      />

      <SelectionTypeEdit
        path="attendee_types/:selectionTypeId/edit"
        onCancel={handleCancel}
        onSave={handleUpdate}
        onDelete={handleDelete}
        selectionTypes={selectionTypes}
        signup={signup}
      />
    </Router>
  )
}

SelectionTypes.propTypes = {
  signup_id: number
}

export default SelectionTypes
